/**
 * Microsoft 365 features and their Dutch explanations. TransectLibre uses
 * application permissions, granted once for the whole tenant by a Microsoft 365
 * administrator: users never connect or consent anything themselves, so a
 * missing permission is always something for the administrators.
 *
 * No imports on purpose: lib/api.ts uses these texts for its error messages.
 */

/** A GraphFeatures flag that depends on an application permission. */
export type GraphFeature = 'directory' | 'sharepoint' | 'calendarSync' | 'freeBusy';

export const GRAPH_FEATURES: readonly GraphFeature[] = ['directory', 'sharepoint', 'calendarSync', 'freeBusy'];

export function isGraphFeature(value: unknown): value is GraphFeature {
  return typeof value === 'string' && (GRAPH_FEATURES as readonly string[]).includes(value);
}

/**
 * Application permissions that enable a feature (any one suffices). Mirrors
 * GRAPH_APP_PERMISSIONS in server/contracts.ts; used to explain why a feature is off.
 */
export const FEATURE_PERMISSIONS: Readonly<Record<GraphFeature, readonly string[]>> = {
  directory: ['User.Read.All', 'Directory.Read.All'],
  sharepoint: ['Sites.Selected', 'Sites.Read.All', 'Sites.ReadWrite.All'],
  calendarSync: ['Calendars.ReadWrite'],
  freeBusy: ['Calendars.ReadBasic', 'Calendars.Read', 'Calendars.ReadWrite'],
};

export function hasPermissionFor(feature: GraphFeature, permissions: readonly string[]): boolean {
  return FEATURE_PERMISSIONS[feature].some((p) => permissions.includes(p));
}

export const FEATURE_TITLE: Record<GraphFeature, string> = {
  directory: 'Collega’s opzoeken',
  sharepoint: 'SharePoint-projectmappen',
  calendarSync: 'Outlook-agenda bijwerken',
  freeBusy: 'Vrij/bezet uit Outlook',
};

const PERMISSION_TEXT: Record<GraphFeature, string> = {
  directory: 'Collega’s opzoeken in Microsoft 365 is nog niet beschikbaar: TransectLibre heeft (nog) geen toestemming om de gebruikers van Transect te lezen.',
  sharepoint: 'SharePoint is nog niet beschikbaar: TransectLibre heeft (nog) geen leestoegang tot de SharePoint-site met de projectmappen.',
  calendarSync: 'De planning in Outlook zetten is nog niet beschikbaar: TransectLibre heeft (nog) geen toestemming om agenda’s bij te werken.',
  freeBusy: 'Vrij/bezet uit Outlook is nog niet beschikbaar: TransectLibre heeft (nog) geen toestemming om agenda’s te lezen.',
};

/** Why a request for `feature` was refused (403 graph_permission). */
export function permissionText(feature: GraphFeature): string {
  return PERMISSION_TEXT[feature];
}

/** Follow-up sentence: admins can check the permissions, everyone else waits for them. */
export function permissionHint(isAdmin: boolean): string {
  return isAdmin
    ? 'Onder Beheer › Systeem zie je welke toestemmingen de app-registratie heeft.'
    : 'Een beheerder zet dit aan zodra de toestemming in Microsoft 365 geregeld is.';
}

export const GRAPH_NOT_CONFIGURED_TEXT =
  'De koppeling met Microsoft 365 is op deze server nog niet ingericht. Vraag de beheerder om de Entra-app-registratie te configureren.';

export const SHAREPOINT_NOT_CONFIGURED_TEXT = 'Er is nog geen SharePoint-site of -bibliotheek ingesteld voor de projectmappen.';
